import { useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { Puzzle, Send, List, Settings, Download, Search, Map, Link2, Copy, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProfileSidebar } from './ProfileSidebar'
import { useGenerateExtToken } from '@/api/hooks/useExtension'
import { PostarTab } from './PostarTab'
import { FilaTab } from './FilaTab'
import { ConfigTab } from './ConfigTab'
import { ExtractorTab } from './ExtractorTab'
import { MapaTab } from './MapaTab'

export function Extension() {
  const { t } = useLanguage()
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([])
  // Link de conexão das extensões (Groups e Reels) sem depender de ter um perfil cadastrado
  const generateToken = useGenerateExtToken()
  const [setupUrl, setSetupUrl] = useState<string>('')
  const [copied, setCopied] = useState(false)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
            <Puzzle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{t('Extensao FB')}</h1>
            <p className="text-xs text-muted-foreground">{t('Gerencie perfis, grupos e posts via extensao')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/blissf-extension.zip" download>
            <Button variant="outline" size="sm" className="gap-1.5 rounded-lg border-border/40">
              <Download className="h-4 w-4" />
              {t('Baixar Extensao')} v2.3.1
            </Button>
          </a>
          <a href="/conectagran-reels-extension.zip" download title={t('Baixa reels de uma pagina em lote (HD) pro PC e/ou pra biblioteca')}>
            <Button variant="outline" size="sm" className="gap-1.5 rounded-lg border-border/40">
              <Download className="h-4 w-4" />
              {t('Extensao Reels')}
            </Button>
          </a>
          <Button variant="outline" size="sm" className="gap-1.5 rounded-lg border-border/40" disabled={generateToken.isPending}
            title={t('Gera o link que conecta as extensoes (Groups e Reels) a esta conta')}
            onClick={() => generateToken.mutate(undefined, { onSuccess: (r) => { setSetupUrl(r.setupUrl); setCopied(false) } })}>
            <Link2 className="h-4 w-4" />
            {generateToken.isPending ? t('Gerando...') : t('Conectar extensao')}
          </Button>
        </div>
      </div>

      {setupUrl && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs space-y-2">
          <div className="font-semibold text-emerald-300">{t('Link de conexao gerado (vale 7 dias). Abra no Chrome onde a extensao esta instalada:')}</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/30 px-2 py-1 rounded break-all text-[10px]">{setupUrl}</code>
            <Button size="sm" variant="ghost" className="h-7 px-2 gap-1" onClick={() => { navigator.clipboard.writeText(setupUrl); setCopied(true) }}>
              <Copy className="h-3.5 w-3.5" />{copied ? t('Copiado') : t('Copiar')}
            </Button>
            <a href={setupUrl} target="_blank" rel="noreferrer">
              <Button size="sm" variant="ghost" className="h-7 px-2 gap-1"><ExternalLink className="h-3.5 w-3.5" />{t('Abrir')}</Button>
            </a>
          </div>
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex gap-6 items-start">
        {/* Sidebar */}
        <div className="w-[320px] shrink-0">
          <ProfileSidebar
            selectedProfileIds={selectedProfileIds}
            onSelectionChange={setSelectedProfileIds}
          />
        </div>

        {/* Main panel */}
        <div className="flex-1 min-w-0">
          <Tabs defaultValue="postar">
            <div className="rounded-2xl bg-muted/40 p-1 backdrop-blur-sm inline-flex">
              <TabsList className="bg-transparent gap-0.5 h-auto p-0">
                <TabsTrigger value="postar" className="rounded-xl data-[state=active]:bg-card text-xs px-3 py-1.5 transition-all duration-200">
                  <Send className="h-3.5 w-3.5 mr-1.5" />{t('Postar')}
                </TabsTrigger>
                <TabsTrigger value="fila" className="rounded-xl data-[state=active]:bg-card text-xs px-3 py-1.5 transition-all duration-200">
                  <List className="h-3.5 w-3.5 mr-1.5" />{t('Fila')}
                </TabsTrigger>
                <TabsTrigger value="config" className="rounded-xl data-[state=active]:bg-card text-xs px-3 py-1.5 transition-all duration-200">
                  <Settings className="h-3.5 w-3.5 mr-1.5" />{t('Config')}
                </TabsTrigger>
                <TabsTrigger value="extrator" className="rounded-xl data-[state=active]:bg-card text-xs px-3 py-1.5 transition-all duration-200">
                  <Search className="h-3.5 w-3.5 mr-1.5" />{t('Extrator')}
                </TabsTrigger>
                <TabsTrigger value="mapa" className="rounded-xl data-[state=active]:bg-card text-xs px-3 py-1.5 transition-all duration-200">
                  <Map className="h-3.5 w-3.5 mr-1.5" />{t('Mapa')}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="postar" className="mt-4">
              <PostarTab selectedProfileIds={selectedProfileIds} />
            </TabsContent>
            <TabsContent value="fila" className="mt-4">
              <FilaTab />
            </TabsContent>
            <TabsContent value="config" className="mt-4">
              <ConfigTab />
            </TabsContent>
            <TabsContent value="extrator" className="mt-4">
              <ExtractorTab selectedProfileIds={selectedProfileIds} />
            </TabsContent>
            <TabsContent value="mapa" className="mt-4">
              <MapaTab selectedProfileIds={selectedProfileIds} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
